import { useCallback, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { joiResolver } from '@hookform/resolvers/joi';

import { useAppContext } from '../../../app/Context';
import { useDrawerContext } from '../../../components/Drawer/Context';
import { getValidClasses } from '../../../db/classes';
import { ClassListItem } from '../../../db/classes/types';
import { CourseListItem } from '../../../db/courses/types';
import Logger from '../../../utils/logger';
import { FormValues } from './types';
import { validationSchema } from './validators';

export interface UseTileClassesForm {
  selectedCourse: CourseListItem;
  onNext: (data: { selectedClass: ClassListItem }) => Promise<void> | void;
}

const useTileClassesForm = ({ selectedCourse, onNext }: UseTileClassesForm) => {
  const { setError } = useAppContext();
  const { onClose } = useDrawerContext();

  const formMethods = useForm<FormValues>({
    resolver: joiResolver(validationSchema),
    defaultValues: {
      selectedClassId: null,
    },
    mode: 'all',
  });

  const { setValue } = formMethods;

  const [isFetchingClasses, setIsFetchingClasses] = useState(true);
  const [classes, setClasses] = useState<ClassListItem[]>([]);

  const fetchClasses = useCallback(async () => {
    setIsFetchingClasses(true);
    try {
      const { classes } = await getValidClasses({ courseId: selectedCourse.courseId });
      // only incharge should be able to see it
      setClasses(classes.filter((cls) => cls.isIncharge));
    } catch (error: any) {
      setError(error.message);
      Logger.error(error);
    } finally {
      setIsFetchingClasses(false);
    }
  }, [selectedCourse.courseId]);

  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  const onSubmit: SubmitHandler<FormValues> = useCallback(
    async (data) => {
      const { selectedClassId } = data;
      await onNext({
        selectedClass: classes.find((cls) => cls.classId === selectedClassId)!,
      });
    },
    [classes, onNext]
  );

  useEffect(() => {
    /** select first class when there is only 1 class */
    if (classes.length !== 1) return;
    const firstClassId = classes[0].classId;
    setValue('selectedClassId', firstClassId, { shouldValidate: true });
  }, [classes, setValue]);

  return { selectedCourse, isFetchingClasses, classes, formMethods, onSubmit, onClose };
};

export default useTileClassesForm;
